import { showRequest, loadingResponse, successResponse, showError } from "../components/common/ResponseMsg/ResponseMsg";
import { toast } from 'react-toastify';
import { getCookie } from "../Utils/Cookies";

function CreateUser(CreateNewUser, userModel, createUserResponse, createNewUserError, period = 5000){
    const newUserResponse = CreateNewUser('api/account/createNewUser', 'POST', 
        userModel, 
        createUserResponse
    );
    // Removed success notification - showRequest(newUserResponse, 'Creating user...', 'Successfully created user', createNewUserError, period);
};

function AccountLogin(Login, userModel, loginResponse, loginError, period = 2000){
    // Show loading toast
    const loadingToastId = loadingResponse('Logging in...');
    
    // Wrap the loginResponse callback to dismiss toast when response is received
    const wrappedLoginResponse = async (data) => {
        try {
            // Dismiss loading toast when response callback is called
            toast.dismiss(loadingToastId);
            // Call original loginResponse if provided
            if(loginResponse && typeof loginResponse === 'function'){
                await loginResponse(data);
            }
        } catch(error) {
            toast.dismiss(loadingToastId);
            throw error;
        }
    };
    
    // Wrap the loginError callback to dismiss toast on error
    const wrappedLoginError = (error) => {
        toast.dismiss(loadingToastId);
        if(loginError && typeof loginError === 'function'){
            loginError(error);
        } else if(loginError){
            toast.error(loginError, {
                position: "bottom-right",
                autoClose: period,
            });
        } else {
            toast.error('Login failed. Please try again.', {
                position: "bottom-right",
                autoClose: period,
            });
        }
    };
    
    const accountLoginResponse = Login('api/account/login', 'POST', {
        "userName": userModel.userName,
        "password": userModel.password,
    }, wrappedLoginResponse);
    
    // Also handle promise rejection as a safety measure
    accountLoginResponse.catch((error) => {
        toast.dismiss(loadingToastId);
        wrappedLoginError(error);
    });
};

function TestAuthorization(TestAuth, testAuthorizationResponse){
    const testResponse = TestAuth('api/account/testAuthorization', 'GET', 
        '', 
        testAuthorizationResponse
    );
};

/** Handles forgot password request - sends only userName to API
 * @param {function} ForgotPasswordApi - The API function to call
 * @param {object} forgotPasswordObj - Object containing userName
 * @param {string} forgotPasswordObj.userName - The username
 * @param {function} forgotPasswordResponse - Optional callback to handle response
 */
function ForgotPassword(ForgotPasswordApi, forgotPasswordObj, forgotPasswordResponse){
    // Extract userName from forgotPasswordObj
    const userName = forgotPasswordObj.userName || forgotPasswordObj.UserName;
    
    // Validate userName is provided
    if(!userName || !userName.trim()){
        showError('Please enter username.');
        return;
    }
    
    // Prepare request object with only userName (as per API requirement)
    const requestObj = {
        userName: userName.trim()
    };
    
    // Wrap the response handler to check IsSuccess and Message
    const wrappedForgotPasswordResponse = (responseData) => {
        try {
            const response = responseData;
            
            // Handle both response structures (direct response or wrapped in data)
            const isSuccess = response?.IsSuccess ?? response?.data?.IsSuccess ?? response?.isSuccess ?? false;
            const message = response?.Message ?? response?.data?.Message ?? response?.message ?? 'An error occurred.';
            
            if(isSuccess){
                // Show success message
                const successMsg = message === 'Valid' ? 'Check your email for the new password and complete the policy.' : message;
                successResponse(successMsg);
                
                // Call the original response handler if provided
                if(forgotPasswordResponse && typeof forgotPasswordResponse === 'function'){
                    forgotPasswordResponse(responseData);
                }
            } else {
                // Show error message (red popup)
                showError(message);
                
                // Call the original response handler if provided (for error handling)
                if(forgotPasswordResponse && typeof forgotPasswordResponse === 'function'){
                    forgotPasswordResponse(responseData);
                }
            }
        } catch(error) {
            console.error('Error handling forgot password response:', error);
            showError('An error has occurred.');
            if(forgotPasswordResponse && typeof forgotPasswordResponse === 'function'){
                forgotPasswordResponse(responseData);
            }
        }
    };
    
    const forgotPasswordResponsePromise = ForgotPasswordApi('api/account/ForgotPassword', 'POST', 
        requestObj, 
        wrappedForgotPasswordResponse
    );
    
    // Handle promise rejection
    if(forgotPasswordResponsePromise && typeof forgotPasswordResponsePromise.catch === 'function'){
        forgotPasswordResponsePromise.catch((error) => {
            console.error('Forgot password API error:', error);
            showError('An error has occurred.');
        });
    }
};

/** Handles reset password request - sends userId/userName, password, and confirmPassword to API
 * @param {function} ResetPassword - The API function to call
 * @param {object} resetPasswordObj - Object containing userId/userName, password, and confirmPassword
 * @param {function} resetPasswordResponse - Callback to handle response
 */
function ResetAccountPassword(ResetPassword, resetPasswordObj, resetPasswordResponse){
    const resetResponse = ResetPassword('api/account/ForgotPassword', 'POST', 
        resetPasswordObj, 
        resetPasswordResponse
    );
};

/**
 * Simple obfuscation/encryption for credentials
 * Uses XOR cipher with a key derived from a simple pattern
 */
function encryptCredentials(data) {
    try {
        const str = JSON.stringify(data);
        const key = 'athos_secure_key_2024'; // Simple key for obfuscation
        let result = '';
        for (let i = 0; i < str.length; i++) {
            result += String.fromCharCode(str.charCodeAt(i) ^ key.charCodeAt(i % key.length));
        }
        // Double encode with base64 to make it less obvious
        return btoa(result);
    } catch (error) {
        console.error('Error obfuscating data:', error);
        return null;
    }
}

/**
 * Deobfuscate/decrypt credentials
 */
function decryptCredentials(obfuscatedData) {
    try {
        // Decode base64 first
        const decoded = atob(obfuscatedData);
        const key = 'athos_secure_key_2024';
        let result = '';
        for (let i = 0; i < decoded.length; i++) {
            result += String.fromCharCode(decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length));
        }
        return JSON.parse(result);
    } catch (error) {
        console.error('Error deobfuscating data:', error);
        return null;
    }
}

/**
 * Stores credentials in localStorage with obfuscation
 * Uses a non-obvious key name to make it harder to find in DevTools
 * @param {object} credentials - The credentials object
 */
function storeCredentialsLocally(credentials) {
    try {
        // Use a non-obvious key name that doesn't reveal its purpose
        const storageKey = 'athos_app_cache_v2';
        const dataToStore = {
            u: credentials.userName || credentials.username, // 'u' for username
            p: credentials.password, // 'p' for password
            t: Date.now() // 't' for timestamp
        };
        
        // Obfuscate the data before storing
        const obfuscated = encryptCredentials(dataToStore);
        if (obfuscated) {
            localStorage.setItem(storageKey, obfuscated);
            // Don't log sensitive information
        }
    } catch (error) {
        console.error('Error storing credentials locally:', error);
    }
}

/**
 * Retrieves credentials from localStorage
 * @returns {object|null} Credentials object or null if not found/expired
 */
function getCredentialsLocally() {
    try {
        const storageKey = 'athos_app_cache_v2';
        const obfuscatedData = localStorage.getItem(storageKey);
        if (!obfuscatedData) {
            return null;
        }
        
        // Deobfuscate the data
        const data = decryptCredentials(obfuscatedData);
        if (!data) {
            return null;
        }
        
        // Check if credentials are still valid (within 2 hours)
        const maxAge = 2 * 60 * 60 * 1000; // 2 hours
        // const maxAge = 20 * 1000; // 20 seconds

        if (Date.now() - data.t > maxAge) {
            localStorage.removeItem(storageKey);
            return null;
        }
        
        return {
            userName: data.u,
            password: data.p
        };
    } catch (error) {
        console.error('Error retrieving credentials locally:', error);
        return null;
    }
}

/**
 * Clears credentials from localStorage
 */
function clearCredentialsLocally() {
    try {
        const storageKey = 'athos_app_cache_v2';
        localStorage.removeItem(storageKey);
    } catch (error) {
        console.error('Error clearing credentials locally:', error);
    }
}

/** Stores user credentials locally (no API call).
 * Credentials are stored in localStorage with obfuscation to make them less visible in DevTools.
 * @param {function} StoreCredentials - Not used (kept for backward compatibility).
 * @param {object} credentials - The credentials object.
 * @param {string} credentials.username - The username.
 * @param {string} credentials.password - The password.
 * @param {function} storeCredentialsResponse - The function to handle the response (called immediately).
 * @param {function} storeCredentialsError - The function to handle the error.
 */
export function StoreCredentials(StoreCredentials, credentials, storeCredentialsResponse, storeCredentialsError){
    try {
        // Store credentials locally with obfuscation
        storeCredentialsLocally(credentials);
        
        // Call response handler immediately (no API call)
        if (storeCredentialsResponse && typeof storeCredentialsResponse === 'function') {
            storeCredentialsResponse({
                status: true,
                data: { message: 'Credentials stored locally' }
            });
        }
    } catch (error) {
        console.error('Error storing credentials:', error);
        if (storeCredentialsError && typeof storeCredentialsError === 'function') {
            storeCredentialsError(error);
        }
    }
};

/** Retrieves stored user credentials from localStorage (no API call).
 * Credentials are retrieved from obfuscated localStorage storage.
 * @param {function} GetCredentials - Not used (kept for backward compatibility).
 * @param {function} getCredentialsResponse - The function to handle the response.
 * @param {function} getCredentialsError - The function to handle the error.
 */
export function GetCredentials(GetCredentials, getCredentialsResponse, getCredentialsError){
    try {
        // 1) Try localStorage first (fast path)
        const credentials = getCredentialsLocally();
        if (credentials && credentials.userName && credentials.password) {
            if (getCredentialsResponse && typeof getCredentialsResponse === 'function') {
                getCredentialsResponse({ status: true, data: credentials });
            }
            return;
        }

        // 2) Fallback: ask the backend if available
        //    The first param is the fetcher (CallAPI) from useHttp; if provided, use it.
        if (typeof GetCredentials === 'function') {
            try {
                const apiResp = GetCredentials('api/account/getCredentials', 'GET', '', null);
                if (apiResp && typeof apiResp.then === 'function') {
                    apiResp.then((resp) => {
                        try {
                            const payload = resp?.data || resp;
                            const userName = payload?.userName || payload?.UserName;
                            const password = payload?.password || payload?.Password;
                            if (userName && password) {
                                // Optionally store locally for next time
                                storeCredentialsLocally({ userName, password });
                                if (getCredentialsResponse && typeof getCredentialsResponse === 'function') {
                                    getCredentialsResponse({ status: true, data: { userName, password } });
                                }
                            } else {
                                if (getCredentialsError && typeof getCredentialsError === 'function') {
                                    getCredentialsError('No credentials available');
                                }
                            }
                        } catch (e) {
                            if (getCredentialsError && typeof getCredentialsError === 'function') {
                                getCredentialsError(e);
                            }
                        }
                    }).catch((err) => {
                        if (getCredentialsError && typeof getCredentialsError === 'function') {
                            getCredentialsError(err);
                        }
                    });
                    return;
                }
            } catch (apiErr) {
                // ignore and fall through to error path
            }
        }

        // No credentials anywhere
        if (getCredentialsError && typeof getCredentialsError === 'function') {
            getCredentialsError('No credentials available');
        }
    } catch (error) {
        console.error('Error retrieving credentials:', error);
        if (getCredentialsError && typeof getCredentialsError === 'function') {
            getCredentialsError(error);
        }
    }
};

/** Clears stored user credentials from localStorage (no API call).
 * @param {function} ClearCredentials - Not used (kept for backward compatibility).
 * @param {function} clearCredentialsResponse - The function to handle the response (called immediately).
 * @param {function} clearCredentialsError - The function to handle the error.
 */
export function ClearCredentials(ClearCredentials, clearCredentialsResponse, clearCredentialsError){
    try {
        // Clear credentials from localStorage
        clearCredentialsLocally();
        
        // Call response handler immediately (no API call)
        if (clearCredentialsResponse && typeof clearCredentialsResponse === 'function') {
            clearCredentialsResponse({
                status: true,
                data: { message: 'Credentials cleared' }
            });
        }
    } catch (error) {
        console.error('Error clearing credentials:', error);
        if (clearCredentialsError && typeof clearCredentialsError === 'function') {
            clearCredentialsError(error);
        }
    }
};

/** Exchange .NET authentication token for React JWT token.
 * This allows seamless authentication when redirecting from .NET to React.
 * @param {function} ExchangeToken - The API function to call
 * @param {object} tokenData - Object containing dotNetToken
 * @param {string} tokenData.dotNetToken - The temporary token from .NET
 * @param {function} exchangeTokenResponse - Callback function to handle successful response
 * @param {function} exchangeTokenError - Callback function to handle errors
 */
function ExchangeDotNetToken(ExchangeToken, tokenData, exchangeTokenResponse, exchangeTokenError){
    const exchangeResponse = ExchangeToken('api/account/exchangeDotNetToken', 'POST', 
        tokenData, 
        exchangeTokenResponse
    );
    
    if(exchangeTokenError && typeof exchangeTokenError === 'function'){
        exchangeResponse.catch(exchangeTokenError);
    }
};

export { CreateUser, AccountLogin, TestAuthorization, ResetAccountPassword, ForgotPassword, ExchangeDotNetToken };